import { Button, Select, Space, Table, Typography, Flex } from "antd";
import { debounce } from "lodash-es";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { StyledButton } from "../../Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import { PaginationComponent } from "../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import SearchFilter from "~components/SearchFilter/SearchFilter";
import countryCodes from "../../config/countryCodes";
import { useDeletePerson } from "../../entities/api";
import { useFetchPersonsPagination } from "../../entities/api/usePersonsAPI";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "../../hooks/useHandlePagination";
import { Genders, Statuses } from "../../utilities/constants";
import AddPersonModal from "~components/AddPersonModal";

const PermissionName = PermissionNames.PERSON;

const columnsone = ({ customerId, sport, organizationId, formatMessage, handleOpenConfirmation }) => {
  return [
    {
      title: formatMessage({
        id: "name.local.full",
        defaultMessage: "Local Full Name",
      }),
      dataIndex: "nameFullLocal",
      width: "70px",
    },
    {
      title: formatMessage({
        id: "gender",
        defaultMessage: "Gender",
      }),
      dataIndex: "gender",
      width: "70px",
    },
    {
      title: formatMessage({
        id: "nationality",
        defaultMessage: "Nationality",
      }),
      dataIndex: "nationality",
      width: "70px",
    },
    {
      title: formatMessage({
        id: "date.of.birth",
        defaultMessage: "Date of Birth (dd/mm/yyyy)",
      }),
      dataIndex: "dob",
      key: "dob",
      width: "70px",
      render: (text, record) => {
        return (
          <div>
            {record?.dob && `${record.dob.split("-")[2]}/${record.dob.split("-")[1]}/${record.dob.split("-")[0]}`}
          </div>
        );
      },
    },
    {
      title: formatMessage({
        id: "status",
        defaultMessage: "status",
      }),
      dataIndex: "status",
      width: "70px",
    },
    {
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/persons/manage/${record.personId}/`}>
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "manage",
                  defaultMessage: "Manage",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`}/>
            </StyledButton>
          </PermissionCheck>
        </div>
      ),
      width: 1,
    },
  ];
};

const statusFilter = [
  {
    text: "ALL",
    value: Statuses.ALL,
  },
  {
    text: "ACTIVE",
    value: Statuses.ACTIVE,
  },
  {
    text: "INACTIVE",
    value: Statuses.INACTIVE,
  },
  {
    text: "PENDING",
    value: Statuses.PENDING,
  },
  {
    text: "DECEASED",
    value: Statuses.DECEASED,
  },
  {
    text: "UNREGISTERED",
    value: Statuses.UNREGISTERED,
  },
];

const GenderFilter = [
  {
    text: "ALL",
    value: "ALL",
  },
  {
    text: Genders.FEMALE,
    value: Genders.FEMALE,
  },
  {
    text: Genders.MALE,
    value: Genders.MALE,
  },
  {
    text: Genders.UNKNOWN,
    value: Genders.UNKNOWN,
  },
];

const NationalityFilter = countryCodes.map((code) => {
  return code[0] ? { text: code[0], value: code[0] } : { text: "ALL", value: "ALL" };
});

const PersonList = ({ columns = null, limit = 1000 }) => {
  const { formatMessage } = useIntl();
  const [addPersonOpenModal, setAddPersonOpenModal] = useState(false);
  const { sport, organizationId, customerId } = useParams();
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(limit);
  const [selected, setSelected] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeFilter, setActiveFilter] = useState(Statuses.ALL);

  const [genderFilter, setGenderFilter] = useState("ALL");
  const [nationalityFilter, setNationalityFilter] = useState("ALL");
  const { mutate: deleteFn, isPending } = useDeletePerson();
  const { data, isLoading } = useFetchPersonsPagination({
    limit: pageSize,
    offset,
    ...(genderFilter && genderFilter !== "ALL" ? { gender: genderFilter } : {}),
    ...(nationalityFilter && nationalityFilter !== "ALL" ? { nationality: nationalityFilter } : {}),
    ...(searchText?.length > 1 ? { nameFullLocalContains: searchText } : { nameFullLocalContains: "" }),
    ...(activeFilter && activeFilter !== "ALL" ? { status: activeFilter } : {}),
  });

  const persons = useMemo(() => data?.data || [], [data?.data]);
  const nextLink = useMemo(() => data?.links?.next, [data?.links?.next]);

  const handleCloseAddPersonModal = useCallback(() => {
    setAddPersonOpenModal((prev) => !prev);
  }, []);

  const handleOpenConfirmation = useCallback(
    (record) => {
      setSelected(record);
      setShowConfirmation(true);
    },
    [setSelected, setShowConfirmation],
  );

  const handleDelete = useCallback(() => {
    deleteFn(selected?.personId);
  }, [deleteFn, selected?.personId]);

  const handlePersonAdded = (data) => {
    if (data?.nameFullLocal) {
      setSearchText(data?.nameFullLocal);
      setActiveFilter(Statuses.ALL);
      setGenderFilter(Statuses.ALL);
      setNationalityFilter(Statuses.ALL);
    }
  }

  const updateSearch = useMemo(() => debounce((searchText) => setSearchText(searchText), 500), [setSearchText]);

  return (
    <Flex vertical gap={8} style={{ marginTop: "16px" }}>
      <Flex align="center" justify="space-between">
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
          <ConfirmationModal
            title="Delete Person"
            setIsModalOpen={setShowConfirmation}
            isModalOpen={showConfirmation}
            handleOk={handleDelete}
            handleCancel={() => {
              setSelected({});
            }}
            okText="Delete"
            okButtonProps={{
              danger: true,
            }}
          >
            <div>{`You are about to delete ${selected.nameFullLocal}. Is this OK?`}</div>
          </ConfirmationModal>
        </PermissionCheck>
        <Typography.Title level={4}>Persons</Typography.Title>
        <Space>
          <div style={{ display: "flex", flexDirection: "column", position: "relative" }}>
            <div style={{ position: "absolute", top: "-20px" }}>Nationality:</div>
            <Select
              value={nationalityFilter}
              onChange={(value) => setNationalityFilter(value)}
              showSearch
              style={{ minWidth: 100 }}
            >
              {NationalityFilter.map((item) => (
                <Select.Option key={item.value} disabled={nationalityFilter === item.value} value={item.value}>
                  {item.text}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div style={{ display: "flex", flexDirection: "column", position: "relative" }}>
            <div style={{ position: "absolute", top: "-20px" }}>Gender:</div>
            <Select value={genderFilter} onChange={(value) => setGenderFilter(value)} style={{ minWidth: 100 }}>
              {GenderFilter.map((item) => (
                <Select.Option key={item.value} disabled={genderFilter === item.value} value={item.value}>
                  {item.text}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", position: "relative" }}>
            <div style={{ position: "absolute", top: "-20px" }}>Status:</div>
            <Select value={activeFilter} onChange={(value) => setActiveFilter(value)} style={{ minWidth: 100 }}>
              {statusFilter.map((item) => (
                <Select.Option key={item.value} disabled={activeFilter === item.value} value={item.value}>
                  {item.text}
                </Select.Option>
              ))}
            </Select>
          </div>

          <SearchFilter
            searchValue={searchText}
            doSearch={updateSearch}
            placeholder={formatMessage({
              id: "person.search.placeholder.2",
              defaultMessage: "Search (2 letters min)",
            })}
          />
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
            <Button type="primary" icon={<i className="fa-solid fa-plus mr-1"></i>} onClick={handleCloseAddPersonModal}>
              Person
            </Button>
            <AddPersonModal open={addPersonOpenModal} handleClose={handleCloseAddPersonModal} onAdd={handlePersonAdded} />
          </PermissionCheck>
        </Space>
      </Flex>
      <Table
        loading={isPending || isLoading}
        size={"small"}
        columns={
          columns
            ? columns
            : columnsone({
              customerId,
              sport,
              organizationId,
              formatMessage,
              handleOpenConfirmation,
            })
        }
        rowKey={"personId"}
        dataSource={persons}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: nextLink ? offset + persons.length + 1 : persons.length + offset,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range}/>
          ),
        }}
      />
    </Flex>
  );
};

export default PersonList;
